/**
 * La préférence d'apparence de l'espace élève — Automatique, Clair ou Sombre —, gardée sur
 * l'appareil.
 *
 * Le même magasin que celui du parent (`createParentAppearanceStore`), sur sa propre clé : **l'élève
 * s'ouvre en Automatique**, il suit le réglage de l'appareil (décision de Wajih, 19/10, qui remplace
 * le Sombre par défaut du 15/10), et garde le choix dans sa feuille « moi ». Seul un choix est écrit
 * sur l'appareil : celui qu'un élève a déjà fait, Sombre compris, ne bouge pas ; celui qui n'a rien
 * choisi passe d'un Sombre imposé au réglage de son appareil.
 *
 * Deux magasins et pas un : un appareil partagé entre un parent qui veut le clair et un élève qui
 * a choisi le sombre doit retrouver chacun le sien.
 *
 * Pas de drapeau à attendre avant de lire : ce crochet n'est monté que par la coquille de l'espace
 * élève, qui n'existe que si l'interrupteur est allumé.
 */
import { useEffect, useSyncExternalStore } from "react"

import { getFromStorage, setToStorage } from "../../../../data/utility/asyncStorageUtils"
import { createParentAppearanceStore, type ParentAppearancePreference } from "./parentAppearancePreference"

export type StudentAppearancePreference = ParentAppearancePreference

/** Clé explicite, distincte de celle du parent. */
export const STUDENT_APPEARANCE_STORAGE_KEY = "masteur.studentSpace.appearance"

/** Le défaut de l'élève : celui du parent. */
export const STUDENT_APPEARANCE_FALLBACK: StudentAppearancePreference = "auto"

const store = createParentAppearanceStore(
    { read: getFromStorage, write: setToStorage },
    { storageKey: STUDENT_APPEARANCE_STORAGE_KEY, fallback: STUDENT_APPEARANCE_FALLBACK }
)

/** Le geste de la section « Apparence » de la feuille « moi ». */
export const setStudentAppearancePreference = store.set

/** La préférence courante, relue à chaque changement ; la lecture de l'appareil part au premier lecteur. */
export function useStudentAppearancePreference(): StudentAppearancePreference {
    const preference = useSyncExternalStore(store.subscribe, store.get, store.get)

    useEffect(() => {
        void store.hydrate()
    }, [])

    return preference
}
